import { computeDeepLink } from "@/components/common/deep-link/deep-link-utils";
import { updateProject } from "@/components/common/project-provider/update-project";
import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import {
  AnnotationCreationData,
  createPostTopic,
  ExternalAnnotationData,
  isExternalAnnotationData,
  NewAttachment,
} from "@/components/ui/annotations/annotation-props";
import { createAttachments } from "@/components/ui/annotations/attachment-mutations";
import {
  addDocumentReferenceToAnnotation,
  getExternalAnnotationProviderName,
  getIElementTypeForBcfIntegrationType,
} from "@/components/ui/annotations/external-annotation-utils";
import { selectProjectIdForIntegrationType } from "@/store/integrations/integrations-selectors";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { assert, generateGUID, GUID } from "@faro-lotv/foundation";
import {
  isIElementAttachmentsGroup,
  isIElementMarkup,
} from "@faro-lotv/ielement-types";
import {
  selectChildDepthFirst,
  selectIElement,
  selectRootId,
} from "@faro-lotv/project-source";
import {
  AnnotationStatus,
  AnnotationType,
  createMutationAddAnnotation,
  createMutationAddExternalMarkup,
  createMutationAddMarkupImagePoint,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback } from "react";

export type ImagePointAnnotationData = {
  /** The iElement on which the annotation was picked */
  parentId: GUID;
  /** The pixel coordinates of the annotation point on the parent image. */
  pixelPosition: {
    x: number;
    y: number;
  };
};

export type CreateImagePointAnnotation = (
  annotation: ImagePointAnnotationData,
  details: AnnotationCreationData,
) => Promise<GUID>;

/** @returns an async function to create an image point annotation */
export function useCreateImagePointAnnotation(): CreateImagePointAnnotation {
  const { projectApiClient } = useApiClientContext();
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();

  const createExternalImagePointAnnotation =
    useCreateExternalImagePointAnnotation();

  const addAttachmentsToAnnotation = useCallback(
    async (annotationId: GUID, attachments: NewAttachment[]) => {
      const store = getState();
      const annotation = selectIElement(annotationId)(store);
      assert(annotation, "Annotation should exist in the store");
      const markup = selectChildDepthFirst(annotation, isIElementMarkup)(store);
      assert(markup, "Annotation should have a Markup as child");
      const attachmentsGroup = selectChildDepthFirst(
        markup,
        isIElementAttachmentsGroup,
      )(store);

      const mutationsAddAttachments = createAttachments(
        annotation.rootId,
        markup.id,
        attachments,
        attachmentsGroup?.id,
      );

      await projectApiClient.applyMutations(mutationsAddAttachments);

      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            ancestorIds: [annotationId],
          },
        }),
      );
    },
    [projectApiClient, getState, dispatch],
  );

  return useCallback(
    async (
      annotationData: ImagePointAnnotationData,
      details: AnnotationCreationData,
    ) => {
      if (isExternalAnnotationData(details)) {
        return await createExternalImagePointAnnotation(
          annotationData,
          details,
        );
      }
      assert(
        !isExternalAnnotationData(details),
        "Image point annotation should not be external",
      );

      const mutationAddAnnotation = createMutationAddAnnotation({
        elementId: annotationData.parentId,
        annotation: {
          id: generateGUID(),
          createdAt: new Date().toISOString(),
          type: AnnotationType.ImagePointAnnotation,
          name: details.title,
          // Mutation will fail with a validation error if the description is the empty string.
          description:
            details.description?.trim() === ""
              ? undefined
              : details.description,
          assigneeId: details.assignee,
          status: isAnnotationStatus(details.status)
            ? details.status
            : undefined,
          dueDate: details.dueDate?.toISOString(),
          labelIds: details.tags?.map((tag) => tag.id),
          pixelPosition: annotationData.pixelPosition,
        },
      });

      await projectApiClient.applyMutations([mutationAddAnnotation]);

      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            ancestorIds: [annotationData.parentId],
          },
        }),
      );

      if (details.newAttachments.length > 0) {
        await addAttachmentsToAnnotation(
          mutationAddAnnotation.annotation.id,
          details.newAttachments,
        );
      }

      return mutationAddAnnotation.annotation.id;
    },
    [
      projectApiClient,
      dispatch,
      addAttachmentsToAnnotation,
      createExternalImagePointAnnotation,
    ],
  );
}

function isAnnotationStatus(
  status?: string | null,
): status is AnnotationStatus {
  return !!status && Object.values<string>(AnnotationStatus).includes(status);
}

type CreateExternalImagePointAnnotation = (
  annotationData: ImagePointAnnotationData,
  details: ExternalAnnotationData,
) => Promise<GUID>;

function useCreateExternalImagePointAnnotation(): CreateExternalImagePointAnnotation {
  const dispatch = useAppDispatch();
  const { bcfServicesApiClient, projectApiClient } = useApiClientContext();
  const appStore = useAppStore();
  const viewContext = useViewRuntimeContext();

  return useCallback(
    async (
      annotationData: ImagePointAnnotationData,
      details: ExternalAnnotationData,
    ) => {
      const markupImagePointId = generateGUID();
      const store = appStore.getState();
      const rootId = selectRootId(store);
      assert(rootId, "Root ID should be defined in the store");

      const externalMarkupId = generateGUID();
      const deepLinkUrl = computeDeepLink(
        store,
        viewContext,
        externalMarkupId,
      ).toString();
      const { externalAnnotationType, screenshotFile } = details;

      const postTopic = createPostTopic({
        data: details,
        id: externalMarkupId,
        deepLinkUrl,
        providerName: getExternalAnnotationProviderName(externalAnnotationType),
      });

      const integrationProjectId = selectProjectIdForIntegrationType(
        externalAnnotationType,
      )(store);
      assert(integrationProjectId, "integration project id should be defined");
      const getTopic = await bcfServicesApiClient.createTopic(
        externalAnnotationType,
        integrationProjectId,
        postTopic,
      );

      const mutationAddMarkupImagePoint = createMutationAddMarkupImagePoint({
        newElementId: markupImagePointId,
        img360Id: annotationData.parentId,
        pixelPosition: annotationData.pixelPosition,
        rootId,
        name: getTopic.guid,
      });

      const addExternalMarkupMutation = createMutationAddExternalMarkup({
        id: externalMarkupId,
        type: getIElementTypeForBcfIntegrationType(externalAnnotationType),
        externalIssueId: getTopic.guid,
        parentId: markupImagePointId,
        rootId,
      });

      await projectApiClient.applyMutations([
        mutationAddMarkupImagePoint,
        addExternalMarkupMutation,
      ]);

      if (screenshotFile) {
        await addDocumentReferenceToAnnotation(
          bcfServicesApiClient,
          externalAnnotationType,
          integrationProjectId,
          getTopic.guid,
          screenshotFile,
        );
      }

      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            ancestorIds: [annotationData.parentId],
          },
        }),
      );

      return markupImagePointId;
    },
    [dispatch, bcfServicesApiClient, projectApiClient, appStore, viewContext],
  );
}
